/**
 * assist.ts — "Write with AI" for single editor fields.
 *
 * One rulebook per field kind (length, one line or many, Markdown or plain
 * text) shared by BOTH sides of the AI boundary:
 *  - the AI Worker enforces it (one repair attempt, then it refuses);
 *  - the CMS Worker re-checks the answer before the browser ever sees it.
 * The browser shows the result as a PREVIEW; a person always presses Accept.
 *
 * What a program can check is checked here (shape, length, no HTML, no new
 * links, no H1/images). What it cannot check (is this fact true?) is surfaced
 * as a WARNING for the human, never silently trusted: numbers the model
 * introduced that were not in the writer's own text are listed.
 */
import { z } from "zod";
import { aiModelChoiceSchema, aiProviderSchema, memoryPackSchema, DEFAULT_AI_PROVIDER } from "./ai.ts";

export const ASSIST_FIELD_KINDS = [
  "title",
  "slug",
  "description",
  "meta_title",
  "meta_description",
  "keywords",
  "body_markdown",
  "faq_question",
  "faq_answer",
  "short_text",
  "long_text",
  "bio",
] as const;
export const assistFieldKindSchema = z.enum(ASSIST_FIELD_KINDS);
export type AssistFieldKind = z.infer<typeof assistFieldKindSchema>;

export const ASSIST_ACTIONS = ["write", "improve", "shorten", "expand", "fix_grammar", "simplify"] as const;
export const assistActionSchema = z.enum(ASSIST_ACTIONS);
export type AssistAction = z.infer<typeof assistActionSchema>;

/** Actions that transform existing text and so need some text to work on. */
export const ASSIST_NEEDS_TEXT: readonly AssistAction[] = ["improve", "shorten", "expand", "fix_grammar", "simplify"];

export interface AssistFieldRule {
  min: number;
  max: number;
  /** false = the field is one line (no line breaks allowed). */
  multiline: boolean;
  /** true = Markdown with ## / ### headings only; false = plain text. */
  markdown: boolean;
  /** A plain-words rule the model is told, and the UI can show. */
  guidance: string;
}

export const ASSIST_FIELD_RULES: Record<AssistFieldKind, AssistFieldRule> = {
  title: { min: 5, max: 110, multiline: false, markdown: false, guidance: "A clear headline of 5 to 110 characters. Plain text, one line. No quotation marks around it." },
  slug: { min: 3, max: 100, multiline: false, markdown: false, guidance: "A web address ending: lowercase letters, numbers and single hyphens only, 3 to 100 characters. Short and readable." },
  description: { min: 20, max: 320, multiline: false, markdown: false, guidance: "A summary of 20 to 320 characters. Plain text, one line." },
  meta_title: { min: 10, max: 70, multiline: false, markdown: false, guidance: "A search-result title of 10 to 70 characters (aim for about 55). Plain text, one line." },
  meta_description: { min: 50, max: 170, multiline: false, markdown: false, guidance: "A search-result description of 50 to 170 characters (aim for about 150). Plain text, one line, ends with a clear benefit or call to action." },
  keywords: { min: 2, max: 400, multiline: false, markdown: false, guidance: "Comma-separated keywords, most important first, no more than 15, no duplicates." },
  body_markdown: { min: 1, max: 30000, multiline: true, markdown: true, guidance: "Markdown using only ## and ### headings (never #). No images, no HTML. Links only if they already exist in the current text." },
  faq_question: { min: 5, max: 300, multiline: false, markdown: false, guidance: "One question in a single line, ending with a question mark." },
  faq_answer: { min: 10, max: 1200, multiline: true, markdown: false, guidance: "A direct answer in plain text, 1 to 4 short sentences." },
  short_text: { min: 1, max: 300, multiline: false, markdown: false, guidance: "Short plain text, one line, at most 300 characters." },
  long_text: { min: 1, max: 2000, multiline: true, markdown: false, guidance: "Plain text, at most 2000 characters. Short paragraphs are fine." },
  bio: { min: 20, max: 1000, multiline: true, markdown: false, guidance: "A professional third-person biography of 2 to 5 sentences. Only facts from the current text; never invent credentials, employers or awards." },
};

export const assistContextSchema = z.object({
  contentType: z.enum(["article", "case_study", "ebook", "author", "other"]).default("other"),
  title: z.string().max(300).optional(),
  keyword: z.string().max(200).optional(),
  audience: z.string().max(300).optional(),
  description: z.string().max(600).optional(),
  /** The start of the article body, so a short field can match what the page is about. */
  bodyExcerpt: z.string().max(4000).optional(),
  /** The article being edited. Used by the CMS only to attribute the AI cost to it; it never reaches the model. */
  contentId: z.string().max(80).optional(),
});
export type AssistContext = z.infer<typeof assistContextSchema>;

const assistBase = {
  field: z.object({ kind: assistFieldKindSchema, label: z.string().trim().min(1).max(80) }),
  action: assistActionSchema,
  /** The field's text right now. Empty is fine for "write". */
  current: z.string().max(30_000).default(""),
  /** An optional steer from the person, e.g. "make it friendlier". Treated as a request about style, never as a rule override. */
  instruction: z.string().trim().max(500).optional(),
  context: assistContextSchema.default({ contentType: "other" }),
};

const needsText = (b: { action: AssistAction; current: string }) => !ASSIST_NEEDS_TEXT.includes(b.action) || b.current.trim().length > 0;
const needsTextMessage = { message: "There is no text in this field to work on yet. Use “Write” instead.", path: ["current"] };

/** CMS Worker -> AI Worker. */
export const assistRequestSchema = z
  .object({
    model: aiModelChoiceSchema.default({ provider: DEFAULT_AI_PROVIDER }),
    ...assistBase,
    memory: memoryPackSchema.default({ rules: [], examples: [], preferredTopics: [], avoidedTopics: [], profile: { voice: "", audience: "", avoid: "" }, knowledge: [] }),
  })
  .refine(needsText, needsTextMessage);
export type AssistRequest = z.infer<typeof assistRequestSchema>;

/** Browser -> CMS Worker. */
export const assistBodySchema = z
  .object({ siteId: z.string().min(1), ...assistBase })
  .refine(needsText, needsTextMessage);
export type AssistBody = z.infer<typeof assistBodySchema>;

export const assistResponseSchema = z.object({
  text: z.string(),
  /** Things a person should look at before accepting. Empty = nothing to flag. */
  warnings: z.array(z.string()).default([]),
  usage: z.object({
    provider: aiProviderSchema,
    model: z.string(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    /** Added by the CMS Worker (the AI service does not know prices). null = no price set for this model. */
    costUsd: z.number().nullable().default(null),
  }),
});
export type AssistResponse = z.infer<typeof assistResponseSchema>;

// ---------------------------------------------------------------------------
// deterministic checks
// ---------------------------------------------------------------------------

const HTML_TAG = /<\/?[a-z][a-z0-9-]*(\s[^>]*)?>/i;
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Lower-case, hyphen-separated; the same idea as the CMS's own slug rule. */
export function toSlug(input: string, maxLength = 100): string {
  return input
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, maxLength)
    .replace(/-+$/g, "");
}

function stripFences(markdown: string): string {
  const out: string[] = [];
  let fence: string | null = null;
  for (const line of markdown.split("\n")) {
    const m = /^ {0,3}(```|~~~)/.exec(line);
    if (fence === null) {
      if (m) fence = m[1] ?? null;
      else out.push(line);
    } else if (m && m[1] === fence) fence = null;
  }
  return out.join("\n");
}

/** Link targets in Markdown (inline, reference and bare URLs). */
export function linkTargets(markdown: string): string[] {
  const found: string[] = [];
  for (const m of markdown.matchAll(/\]\(\s*<?([^\s)>]+)/g)) found.push(m[1] ?? "");
  for (const m of markdown.matchAll(/^ {0,3}\[[^\]\n]+\]:[ \t]*<?(\S+?)>?\s*$/gm)) found.push(m[1] ?? "");
  for (const m of markdown.matchAll(/\bhttps?:\/\/[^\s<>()[\]]+/gi)) found.push(m[0].replace(/[.,;:!?'"]+$/, ""));
  return [...new Set(found.filter(Boolean))];
}

const NUMBER = /\d[\d,.]*\d|\d/g;
const numbersIn = (s: string): Set<string> => new Set((s.match(NUMBER) ?? []).map((n) => n.replace(/[,.]+$/, "")));

export interface AssistCheckInput {
  kind: AssistFieldKind;
  action: AssistAction;
  current: string;
  instruction?: string | undefined;
  context?: Partial<AssistContext> | undefined;
}

export interface AssistCheckResult {
  /** The cleaned text (quotes/fences stripped, slug and keywords normalised). */
  text: string;
  /** Hard failures. If any, the answer must not be used. */
  issues: string[];
  /** Soft flags for the human reviewer. */
  warnings: string[];
}

/** Cleans and checks one model answer for one field. Pure: no I/O. */
export function checkAssistText(rawText: string, input: AssistCheckInput): AssistCheckResult {
  const rule = ASSIST_FIELD_RULES[input.kind];
  const issues: string[] = [];
  const warnings: string[] = [];
  let text = rawText.replace(/\r\n?/g, "\n").trim();

  // whole answer wrapped in a code fence
  const fenced = /^```[a-z]*\n([\s\S]*?)\n```$/i.exec(text);
  if (fenced) text = (fenced[1] ?? "").trim();

  if (!rule.multiline) {
    // models sometimes quote single-line answers
    const quoted = /^(["“'`])([\s\S]*)(["”'`])$/.exec(text);
    if (quoted && !(quoted[2] ?? "").includes(quoted[1] ?? "\u0000")) text = (quoted[2] ?? "").trim();
    if (text.includes("\n")) issues.push(`This field is one line, but the answer has ${text.split("\n").length} lines.`);
  }

  if (input.kind === "slug") {
    text = toSlug(text, rule.max);
    if (!SLUG.test(text)) issues.push("The slug must use lowercase letters, numbers and single hyphens.");
  }

  if (input.kind === "keywords") {
    const seen = new Set<string>();
    const parts = text
      .split(/[,\n;]/)
      .map((k) => k.trim().replace(/^[-*•\d.)\s]+/, ""))
      .filter((k) => {
        const key = k.toLowerCase();
        if (!k || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    if (parts.length > 15) issues.push("Use 15 keywords or fewer.");
    text = parts.join(", ");
  }

  if (text.length < rule.min) issues.push(`The answer is too short (${text.length} characters; at least ${rule.min}).`);
  if (text.length > rule.max) issues.push(`The answer is too long (${text.length} characters; at most ${rule.max}).`);

  if (HTML_TAG.test(text)) issues.push("Remove HTML tags; this field takes plain text or Markdown only.");
  if (/(^|\s)(javascript|data):/i.test(text)) issues.push("Remove javascript: and data: links.");

  if (rule.markdown) {
    const body = stripFences(text);
    if (/^ {0,3}#(?!#)\s/m.test(body)) issues.push("Do not use a top-level # heading (the page title is separate). Use ## and ###.");
    if (/^ {0,3}#{4,}\s/m.test(body)) issues.push("Use only ## and ### headings.");
    if (/!\[[^\]]*\]\(/.test(body)) issues.push("Do not add images.");
    const allowed = new Set(linkTargets(input.current));
    const added = linkTargets(body).filter((l) => !allowed.has(l));
    if (added.length > 0) issues.push(`Do not add new links (found: ${[...new Set(added)].slice(0, 3).join(", ")}). Keep only links already in the text.`);
  } else {
    if (/^\s{0,3}#{1,6}\s/m.test(text) || /\*\*[^*]+\*\*/.test(text) || /^\s*[-*]\s+\S/m.test(text)) {
      issues.push("Use plain text only: no headings, bold or bullet characters in this field.");
    }
    if (linkTargets(text).some((l) => !linkTargets(input.current).includes(l))) issues.push("Do not add web links to this field.");
  }

  // Numbers the writer did not provide are flagged for the human (cannot be verified by a program).
  const known = numbersIn(
    [input.current, input.instruction ?? "", input.context?.title ?? "", input.context?.keyword ?? "", input.context?.description ?? "", input.context?.bodyExcerpt ?? ""].join(" ")
  );
  if (input.kind !== "slug") {
    const invented = [...numbersIn(text)].filter((n) => !known.has(n));
    if (invented.length > 0) {
      warnings.push(`Contains numbers that were not in your text: ${invented.slice(0, 6).join(", ")}. Check they are true before accepting.`);
    }
  }
  if (input.action === "write" && input.current.trim() === "" && !input.context?.title && !input.context?.keyword && !input.context?.bodyExcerpt && !input.instruction) {
    warnings.push("There was very little to go on (no title, keyword or notes), so this is a generic suggestion.");
  }

  return { text, issues, warnings };
}
